import type { ExtensionRuntimeType, ExtensionSourceInfo } from "@magic-md/extension-sdk";
import { useT } from "@magic-md/i18n/react";
import {
    Activity,
    Blocks,
    Cloud,
    HardDrive,
    Loader2,
    Package,
    Search,
    X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

export type ExtensionSourceFilter = "all" | "local" | "cloud";

export interface ExtensionListItemModel {
    id: string;
    title: string;
    icon?: string;
    description?: string;
    category?: string;
    version?: string;
    runtimeType?: ExtensionRuntimeType;
    source?: ExtensionSourceInfo;
    active?: boolean;
    hasPanel?: boolean;
    readOnlyLabel?: string;
    loading?: boolean;
    disabled?: boolean;
    searchText?: string[];
    toggle?: {
        checked: boolean;
        disabled?: boolean;
        loading?: boolean;
        onChange: (checked: boolean) => void;
    };
}

export const EXTENSION_SOURCE_FILTERS: ExtensionSourceFilter[] = [
    "all",
    "local",
    "cloud",
];

export function isCloudExtensionListItem(
    item: Pick<ExtensionListItemModel, "runtimeType" | "source">,
): boolean {
    return (
        item.runtimeType === "cloudflare-worker" ||
        item.source?.distributionKind === "cloud-worker" ||
        item.source?.distributionKind === "cloud-bundle" ||
        !!item.source?.manifestUrl ||
        !!item.source?.uiBundleUrl ||
        !!item.source?.invokeUrl
    );
}

export function filterExtensionListItemsBySource<T extends ExtensionListItemModel>(
    items: T[],
    sourceFilter: ExtensionSourceFilter,
): T[] {
    if (sourceFilter === "all") return items;
    return items.filter((item) => {
        const isCloud = isCloudExtensionListItem(item);
        return sourceFilter === "cloud" ? isCloud : !isCloud;
    });
}

function countExtensionSources(
    items: ExtensionListItemModel[],
): Record<ExtensionSourceFilter, number> {
    let local = 0;
    let cloud = 0;
    for (const item of items) {
        if (isCloudExtensionListItem(item)) cloud += 1;
        else local += 1;
    }
    return { all: items.length, local, cloud };
}

function extensionSourceFilterLabel(
    t: ReturnType<typeof useT>,
    filter: ExtensionSourceFilter,
): string {
    if (filter === "cloud") return t("extensionPanel.filter.cloud");
    if (filter === "local") return t("extensionPanel.filter.local");
    return t("extensionPanel.filter.all");
}

export function ExtensionList({
    items,
    selectedId,
    onSelect,
    title,
    searchPlaceholder,
    emptyMessage,
    noMatchesMessage,
    searchQuery,
    onSearchChange,
    sourceFilter,
    onSourceFilterChange,
    showSearch = true,
    showSourceFilter = true,
    className,
}: {
    items: ExtensionListItemModel[];
    selectedId?: string | null;
    onSelect?: (id: string) => void;
    title?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    noMatchesMessage?: string;
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
    sourceFilter?: ExtensionSourceFilter;
    onSourceFilterChange?: (filter: ExtensionSourceFilter) => void;
    showSearch?: boolean;
    showSourceFilter?: boolean;
    className?: string;
}) {
    const t = useT("editor");
    const [internalSearchQuery, setInternalSearchQuery] = useState("");
    const [internalSourceFilter, setInternalSourceFilter] =
        useState<ExtensionSourceFilter>("all");
    const activeSearch = searchQuery ?? internalSearchQuery;
    const activeSourceFilter = sourceFilter ?? internalSourceFilter;
    const setSearchQuery = onSearchChange ?? setInternalSearchQuery;
    const setSourceFilter = onSourceFilterChange ?? setInternalSourceFilter;
    const counts = useMemo(() => countExtensionSources(items), [items]);
    const filteredItems = useMemo(() => {
        const q = activeSearch.trim().toLowerCase();
        return filterExtensionListItemsBySource(items, activeSourceFilter).filter((item) => {
            if (!q) return true;
            return buildExtensionSearchTerms(item).some((term) =>
                term.toLowerCase().includes(q),
            );
        });
    }, [activeSearch, activeSourceFilter, items]);
    const hasFilters = activeSearch.trim().length > 0 || activeSourceFilter !== "all";

    return (
        <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
            {(showSearch || showSourceFilter || title) ? (
                <div className="border-b border-border px-2 py-2">
                    {title ? (
                        <div className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {title}
                        </div>
                    ) : null}
                    {(showSearch || showSourceFilter) ? (
                        <div className="flex items-center gap-1.5">
                            {showSearch ? (
                                <div className="relative min-w-0 flex-1">
                                    <Search
                                        size={12}
                                        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/50"
                                    />
                                    <input
                                        type="text"
                                        className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-7 text-xs placeholder:text-muted-foreground/40 outline-none transition-colors focus:border-primary"
                                        placeholder={searchPlaceholder ?? t("extensionPanel.searchPlaceholder")}
                                        value={activeSearch}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                    />
                                    {activeSearch ? (
                                        <button
                                            type="button"
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 transition-colors hover:text-foreground"
                                            onClick={() => setSearchQuery("")}
                                        >
                                            <X size={12} />
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                            {showSourceFilter ? (
                                <div className="inline-flex shrink-0 items-center rounded-md border border-border/70 bg-muted/20 p-0.5">
                                    {EXTENSION_SOURCE_FILTERS.map((filter) => {
                                        const active = activeSourceFilter === filter;
                                        return (
                                            <button
                                                key={filter}
                                                type="button"
                                                onClick={() => setSourceFilter(filter)}
                                                className={cn(
                                                    "inline-flex h-6 min-w-8 shrink-0 items-center justify-center gap-1 rounded px-1.5 text-[10px] transition-colors",
                                                    active
                                                        ? "bg-background text-primary shadow-sm"
                                                        : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
                                                )}
                                                title={extensionSourceFilterLabel(t, filter)}
                                                aria-label={extensionSourceFilterLabel(t, filter)}
                                            >
                                                <ExtensionFilterIcon filter={filter} />
                                                <span className="tabular-nums opacity-70">
                                                    {counts[filter]}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto">
                {filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
                        <Blocks size={16} className="text-muted-foreground/40" />
                        <p className="text-[10px] text-muted-foreground/50">
                            {hasFilters
                                ? noMatchesMessage ?? t("extensionPanel.noMatching")
                                : emptyMessage ?? t("engine.extensions.empty")}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-border/30">
                        {filteredItems.map((item) => (
                            <ExtensionListItem
                                key={item.id}
                                item={item}
                                selected={selectedId === item.id}
                                onSelect={onSelect}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ExtensionFilterIcon({ filter }: { filter: ExtensionSourceFilter }) {
    if (filter === "cloud") return <Cloud size={12} />;
    if (filter === "local") return <HardDrive size={12} />;
    return <Package size={12} />;
}

function ExtensionListItem({
    item,
    selected,
    onSelect,
}: {
    item: ExtensionListItemModel;
    selected: boolean;
    onSelect?: (id: string) => void;
}) {
    const t = useT("editor");
    const interactive = !!onSelect && !item.disabled;
    const isCloud = isCloudExtensionListItem(item);

    return (
        <button
            type="button"
            onClick={() => {
                if (interactive) onSelect(item.id);
            }}
            className={cn(
                "flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors",
                selected ? "bg-primary/5" : interactive ? "hover:bg-muted/40" : "cursor-default",
                item.disabled && "opacity-75",
            )}
        >
            {item.toggle ? (
                <label
                    className="mt-0.5 flex items-center"
                    onClick={(event) => event.stopPropagation()}
                >
                    <input
                        type="checkbox"
                        checked={item.toggle.checked}
                        disabled={item.toggle.disabled || item.toggle.loading}
                        onChange={(event) => item.toggle?.onChange(event.target.checked)}
                    />
                </label>
            ) : null}
            <span className="mt-0.5 shrink-0 text-sm leading-none">
                {item.icon ?? "🧩"}
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span className="truncate text-xs font-medium text-foreground/80">
                            {item.title}
                        </span>
                        {item.active ? (
                            <Activity
                                size={10}
                                className="shrink-0 theme-accent-text"
                                style={getThemeAccentStyle("success")}
                            />
                        ) : null}
                        {item.hasPanel ? (
                            <span
                                title={t("extensionPanel.hasSidebarPanel")}
                                className="shrink-0 rounded border border-border/60 bg-muted/40 px-1 py-0.5 text-[10px] text-muted-foreground"
                            >
                                UI
                            </span>
                        ) : null}
                        {item.readOnlyLabel ? (
                            <span className="shrink-0 rounded border border-border/60 bg-muted/40 px-1 py-0.5 text-[10px] text-muted-foreground">
                                {item.readOnlyLabel}
                            </span>
                        ) : null}
                        {item.loading || item.toggle?.loading ? (
                            <Loader2 size={12} className="shrink-0 animate-spin text-muted-foreground" />
                        ) : null}
                    </div>
                    {isCloud ? (
                        <span
                            title={t("extensionPanel.cloudSource")}
                            className="ml-auto inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/5 text-primary"
                        >
                            <Cloud size={11} />
                        </span>
                    ) : null}
                </div>
                {item.description ? (
                    <div
                        className="mt-1 truncate text-xs text-muted-foreground"
                        title={item.description}
                    >
                        {item.description}
                    </div>
                ) : null}
            </div>
        </button>
    );
}

function buildExtensionSearchTerms(item: ExtensionListItemModel): string[] {
    return [
        item.id,
        item.title,
        item.description,
        item.category,
        item.version,
        item.runtimeType,
        item.source?.distributionKind,
        item.source?.manifestUrl,
        item.source?.uiBundleUrl,
        item.source?.invokeUrl,
        item.source?.localPath,
        item.source?.manifestPath,
        item.source?.uiBundlePath,
        ...(item.searchText ?? []),
    ].filter((value): value is string => !!value);
}
